import { values } from 'lodash';
import { denormalize } from 'normalizr';
import { createSelector } from 'reselect';
import { CALL_API, HTTP_METHODS } from '../middlewares/api.js';
import { role as roleSchema } from '../lib/schema.js';
import { selectCurrentOrganizationId } from './auth.js';
import { deleteEntities, selectEntities } from './entities.js';

// API Actions

const CREATE_ROLE_REQUEST = 'changesync/roles/CREATE_ROLE_REQUEST';
const CREATE_ROLE_SUCCESS = 'changesync/roles/CREATE_ROLE_SUCCESS';
const CREATE_ROLE_FAILURE = 'changesync/roles/CREATE_ROLE_FAILURE';
const RETRIEVE_ROLE_REQUEST = 'changesync/roles/RETRIEVE_ROLE_REQUEST';
const RETRIEVE_ROLE_SUCCESS = 'changesync/roles/RETRIEVE_ROLE_SUCCESS';
const RETRIEVE_ROLE_FAILURE = 'changesync/roles/RETRIEVE_ROLE_FAILURE';
const DELETE_ROLE_REQUEST = 'changesync/roles/DELETE_ROLE_REQUEST';
const DELETE_ROLE_SUCCESS = 'changesync/roles/DELETE_ROLE_SUCCESS';
const DELETE_ROLE_FAILURE = 'changesync/roles/DELETE_ROLE_FAILURE';
const RETRIEVE_ROLES_REQUEST = 'changesync/roles/RETRIEVE_ROLES_REQUEST';
const RETRIEVE_ROLES_SUCCESS = 'changesync/roles/RETRIEVE_ROLES_SUCCESS';
const RETRIEVE_ROLES_FAILURE = 'changesync/roles/RETRIEVE_ROLES_FAILURE';
const SEARCH_ROLES_REQUEST = 'changesync/roles/SEARCH_ROLES_REQUEST';
const SEARCH_ROLES_SUCCESS = 'changesync/roles/SEARCH_ROLES_SUCCESS';
const SEARCH_ROLES_FAILURE = 'changesync/roles/SEARCH_ROLES_FAILURE';

const UPDATE_SEARCH_QUERY = 'changesync/roles/UPDATE_SEARCH_QUERY';
const SEARCH_ROLES = 'changesync/roles/SEARCH_ROLES';

const initialState = {
  retrieveRole: {
    pending: false,
    success: false,
    error: null,
  },
  retrieveRoles: {
    pending: false,
    success: false,
    error: null,
  },

  createRoles: {
    pending: false,
    success: false,
    error: null,
  },

  searchRoles: {
    rolesIds: [],
    query: '',
    pending: false,
    success: false,
    error: null,
  },
};

// Reducer

export default (state = initialState, action) => {
  switch (action.type) {
    case CREATE_ROLE_REQUEST:
      return {
        ...state,
        createRole: {
          ...state.createRole,
          pending: true,
          success: false,
          error: null,
        },
        searchRoles: {
          ...state.searchRoles,
          rolesIds: [],
        },
      };
    case CREATE_ROLE_SUCCESS:
      return {
        ...state,
        createRole: {
          ...state.createRole,
          pending: false,
          success: true,
        },
      };
    case CREATE_ROLE_FAILURE:
      return {
        ...state,
        createRole: {
          ...state.createRole,
          pending: false,
          error: action.error,
        },
      };
    case RETRIEVE_ROLE_REQUEST:
      return {
        ...state,
        retrieveRole: {
          ...state.retrieveRole,
          pending: true,
          success: false,
          error: null,
        },
        searchRoles: {
          ...state.searchRoles,
          rolesIds: [],
        },
      };
    case RETRIEVE_ROLE_SUCCESS:
      return {
        ...state,
        retrieveRole: {
          ...state.retrieveRole,
          pending: false,
          success: true,
        },
      };
    case RETRIEVE_ROLE_FAILURE:
      return {
        ...state,
        retrieveRole: {
          ...state.retrieveRole,
          pending: false,
          error: action.error,
        },
      };
    case RETRIEVE_ROLES_REQUEST:
      return {
        ...state,
        retrieveRoles: {
          ...state.retrieveRoles,
          pending: true,
          success: false,
          error: null,
        },
      };
    case RETRIEVE_ROLES_SUCCESS:
      return {
        ...state,
        retrieveRoles: {
          ...state.retrieveRoles,
          pending: false,
          success: true,
        },
      };
    case RETRIEVE_ROLES_FAILURE:
      return {
        ...state,
        retrieveRoles: {
          ...state.retrieveRoles,
          pending: false,
          error: action.error,
        },
      };

    case SEARCH_ROLES_REQUEST:
      return {
        ...state,
        searchRoles: {
          ...state.searchRoles,
          pending: true,
          success: false,
          error: null,
        },
      };

    case SEARCH_ROLES_SUCCESS:
      return {
        ...state,
        searchRoles: {
          ...state.searchRoles,
          rolesIds: action.payload.result,
          query: action.meta.query,
          pending: false,
          success: true,
        },
      };

    case UPDATE_SEARCH_QUERY:
      return {
        ...state,
        searchQuery: action.payload,
      };

    default:
      return state;
  }
};

export const createRole = (userId, role) => async (dispatch, getState) => {
  const organizationId = selectCurrentOrganizationId(getState());
  const entitiesToBeCleaned = Object.keys(getState().entities.roles).map(
    (key) => `roles.${key}`
  );
  const project = { ...role }; // rename for api requirements
  await dispatch({
    [CALL_API]: {
      types: [CREATE_ROLE_REQUEST, CREATE_ROLE_SUCCESS, CREATE_ROLE_FAILURE],
      endpoint: `organizations/${organizationId}/users/${userId}/permissions`,
      method: HTTP_METHODS.POST,
      schema: roleSchema,
      body: { project },
    },
  });
  return dispatch(
    deleteEntities([...entitiesToBeCleaned, `roles.${project.id}`])
  );
};

export const updateRole = (userId, projectId, role) => (dispatch, getState) => {
  const organizationId = selectCurrentOrganizationId(getState());
  const project = { ...role }; // rename for api requirements
  return dispatch({
    [CALL_API]: {
      types: [CREATE_ROLE_REQUEST, CREATE_ROLE_SUCCESS, CREATE_ROLE_FAILURE],
      endpoint: `organizations/${organizationId}/users/${userId}/permissions/${projectId}`,
      method: HTTP_METHODS.PUT,
      schema: roleSchema,
      body: { project },
    },
  });
};

export const deleteRole = (userId, projectId) => async (dispatch, getState) => {
  const organizationId = selectCurrentOrganizationId(getState());

  await dispatch({
    [CALL_API]: {
      types: [DELETE_ROLE_REQUEST, DELETE_ROLE_SUCCESS, DELETE_ROLE_FAILURE],
      endpoint: `organizations/${organizationId}/users/${userId}/permissions/${projectId}`,
      method: HTTP_METHODS.DELETE,
    },
    meta: { projectId },
  });

  return dispatch(deleteEntities([`roles.${projectId}`]));
};

export const retrieveRole = (userId, projectId) => (dispatch, getState) => {
  const organizationId = selectCurrentOrganizationId(getState());

  return dispatch({
    [CALL_API]: {
      types: [
        RETRIEVE_ROLE_REQUEST,
        RETRIEVE_ROLE_SUCCESS,
        RETRIEVE_ROLE_FAILURE,
      ],
      endpoint: `organizations/${organizationId}/users/${userId}/permissions/${projectId}`,
      method: HTTP_METHODS.GET,
      schema: roleSchema,
    },
  });
};

export const retrieveRoles = (userId) => (dispatch, getState) => {
  const organizationId = selectCurrentOrganizationId(getState());

  return dispatch({
    [CALL_API]: {
      types: [
        RETRIEVE_ROLES_REQUEST,
        RETRIEVE_ROLES_SUCCESS,
        RETRIEVE_ROLES_FAILURE,
      ],
      endpoint: `organizations/${organizationId}/users/${userId}/permissions`,
      method: HTTP_METHODS.GET,
      schema: [roleSchema],
    },
  });
};

export const searchRoles = (userId, query) => (dispatch, getState) => {
  const organizationId = selectCurrentOrganizationId(getState());

  const searchParam = query ? `?search=&search=${query}` : '';

  return dispatch({
    [CALL_API]: {
      types: [SEARCH_ROLES_REQUEST, SEARCH_ROLES_SUCCESS, SEARCH_ROLES_FAILURE],
      endpoint: `organizations/${organizationId}/users/${userId}/permissions${searchParam}`,
      method: HTTP_METHODS.GET,
      schema: [roleSchema],
    },
    meta: {
      debounce: {
        time: 400,
        key: SEARCH_ROLES,
      },
    },
  });
};

export const updateSearchQuery = (query) => ({
  type: UPDATE_SEARCH_QUERY,
  payload: query,
});

// Selectors

export const selectRoles = (state) => values(state.entities.roles);

export const selectRole = (id) => (state) =>
  denormalize(id, roleSchema, state.entities);

export const selectSearchRolesState = (state) => state.roles.searchRoles;

const selectSearchRolesIds = createSelector(
  selectSearchRolesState,
  (state) => state.rolesIds
);

export const selectSearchRolesResults = createSelector(
  selectSearchRolesIds,
  selectEntities,
  (rolesIds, entities) => denormalize(rolesIds, [roleSchema], entities)
);
